import { Card, CardHeader, Badge, Typography } from '@statsbomb/kitbag-components';
import { useAtomValue } from 'jotai';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { unwrappedGameTeamNameAtom } from '@/atoms/game/game';
import { COMING_SOON_WIDGETS, HEADING_ONLY_NAVIGABLE_WIDGETS } from '@/consts/widget';
import { useRadarTemplateName } from '@/hooks/useRadarTemplateName';
import { TitleSettings } from '@/types/userConfigs';
import { WidgetVisType } from '@/types/widget';

interface DashboardWidgetProps {
  children: React.ReactNode;
  navigationPath: string;
  vis: WidgetVisType;
  titleSettings: TitleSettings;
}

interface DashboardWidgetWrapperProps {
  children: React.ReactNode;
  navigationPath: string;
  hasHeadingOnlyNavigation: boolean;
}

const WidgetWrapper = ({ navigationPath, hasHeadingOnlyNavigation, children }: DashboardWidgetWrapperProps) =>
  navigationPath && !hasHeadingOnlyNavigation ? (
    <Link to={navigationPath} className="[&_header]:underline [&:not(:hover)_header]:opacity-sb-secondary">
      {children}
    </Link>
  ) : (
    children
  );

const WidgetHeader = ({ navigationPath, hasHeadingOnlyNavigation, children }: DashboardWidgetWrapperProps) =>
  navigationPath && hasHeadingOnlyNavigation ? (
    <Link to={navigationPath} className="underline [&:not(:hover)]:opacity-sb-secondary">
      <CardHeader>{children}</CardHeader>
    </Link>
  ) : (
    <CardHeader>{children}</CardHeader>
  );

export const formatWidgetTitle = (
  formattedTitle: string,
  vis: WidgetVisType,
  title: string,
  radarTemplateName: string,
  gameTeamName: string,
): string => {
  // Player radar widgets show the radar template name
  if (vis === 'radar' && title === 'playerRadar' && radarTemplateName) {
    return `${formattedTitle} - ${radarTemplateName}`;
  }

  // Game widgets show the team name
  if (gameTeamName) {
    return `${formattedTitle} - ${gameTeamName}`;
  }

  return formattedTitle;
};

const useGetWidgetTitle = (radarTemplateName: string, vis: WidgetVisType, titleSettings: TitleSettings) => {
  const { t } = useTranslation('statsbombSharedConfigs');
  const gameTeamName = useAtomValue(unwrappedGameTeamNameAtom);

  const { title } = titleSettings;
  const formattedTitle = titleSettings.translate ? t(title) : title;

  return formatWidgetTitle(formattedTitle, vis, title, radarTemplateName, gameTeamName);
};

export const DashboardWidget = ({ vis, children, navigationPath, titleSettings }: DashboardWidgetProps) => {
  const { t } = useTranslation('general');
  const radarTemplateName = useRadarTemplateName();

  const title = useGetWidgetTitle(radarTemplateName, vis, titleSettings);
  const hasHeadingOnlyNavigation = HEADING_ONLY_NAVIGABLE_WIDGETS.includes(vis);
  const isComingSoon = COMING_SOON_WIDGETS.includes(vis);

  return (
    <WidgetWrapper navigationPath={navigationPath} hasHeadingOnlyNavigation={hasHeadingOnlyNavigation}>
      <Card>
        <WidgetHeader navigationPath={navigationPath} hasHeadingOnlyNavigation={hasHeadingOnlyNavigation}>
          <Typography variant="headingMedium" as="h3" className="w-full flex items-center gap-2">
            <div className="flex-1 truncate" title={title}>
              {title}
            </div>
            <div>
              {isComingSoon && (
                <Badge variant="subtle" hasIcon={false}>
                  {t('inDevelopment')}
                </Badge>
              )}
            </div>
          </Typography>
        </WidgetHeader>
        {children}
      </Card>
    </WidgetWrapper>
  );
};
